import {
  Button,
  Dialog,
  DialogActions,
  Typography,
  makeStyles,
} from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons"

import { colors } from "../../../utils/theme"
import { planTypeToString } from "../../../utils/safetyPlanUtils"
import { Enum_Safety_Plan_Type_Enum } from "../../../generated/graphql"

interface SafetyPlanFinishedDialogProps {
  planType: Enum_Safety_Plan_Type_Enum | undefined
  open: boolean
  onClose: () => void
}

/**
 * Confirmation dialog shown after a practitioner successfully saves a safety plan.
 *
 * @param open - Whether the dialog is visible.
 * @param onClose - Callback invoked when the user dismisses the dialog.
 */
export default function SafetyPlanFinishedDialog({
  planType,
  open,
  onClose,
}: SafetyPlanFinishedDialogProps) {
  const classes = useStyles()

  return (
    <Dialog open={open} onClose={onClose}>
      <div className={classes.completedModal}>
        <div className={classes.completedContainer}>
          <FontAwesomeIcon
            icon={faCircleCheck}
            className={classes.completedIcon}
          />

          <Typography variant="h2" className={classes.completedTitle}>
            Your {planTypeToString(planType, true)} is Ready for Review
          </Typography>
          <Typography
            align="center"
            style={{ color: colors.safetyPlan.ink, lineHeight: "20px" }}
          >
            Great work! Next please <b>review and confirm</b> the contents of
            your {planTypeToString(planType, false)}. Use the edit button in
            each section if you need to change any of your answers, or you’re
            all done.
          </Typography>
        </div>

        <DialogActions>
          <Button fullWidth className={classes.planDoneBtn} onClick={onClose}>
            Got it!
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  )
}

const useStyles = makeStyles(() => ({
  completedModal: {
    borderRadius: "12px",
    background: colors.neutral.white,
    boxShadow: "0px 4px 68px 24px rgba(0, 0, 0, 0.16)",
    display: "flex",
    width: "367px",
    padding: "32px 24px",
    flexDirection: "column",
    gap: "16px",
  },
  completedContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  completedIcon: {
    width: "70.523px",
    height: "70.523px",
    color: colors.primary.shadow,
  },
  completedTitle: {
    color: colors.safetyPlan.ink,
    textAlign: "center",
    lineHeight: "40px",
  },
  planDoneBtn: {
    display: "flex",
    height: "52px",
    padding: "4px 32px",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "200px",
    textTransform: "none",
    color: colors.neutral.white,
    background: colors.primary.shadow,
    "&:hover": {
      background: colors.primary.midnight,
    },
  },
}))
